'use client';

import { FunctionComponent, useState, useEffect } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { useTranslate } from '@xFrame4/components/Hooks';
import { NetopiaPaymentGateway } from '@xFrame4/helpers/PaymentGateway';
import { formatPrice } from '@xFrame4/common/Utils';
import { formatDateUserFriendly } from '@xFrame4/common/Functions';
import PaymentLink, { PAYMENT_LINK_ORDER_ID_PREFIX } from '@business/payments/PaymentLink';
import { PaymentLinkStatus } from '@business/payments/generated/PaymentLink.generated';
import UrlHelper from 'frontend_business/seo/UrlHelper';
import PaymentLinkCard from './PaymentLinkCard';
import PaymentDetailRow from './PaymentDetailRow';
import PaymentDetailsSection from './PaymentDetailsSection';
import PaymentContactSupport from './PaymentContactSupport';

export interface PaymentLinkStartPaymentProps
{
    uniqueLinkId: string;
    language: string;
}

/**
 * Component: Payment link client component that displays payment information and handles redirect to payment gateway.
 * The component always receives a props argument, not a destructured one.
 */
const PaymentLinkStartPayment: FunctionComponent<PaymentLinkStartPaymentProps> = (props) =>
{
    const t = useTranslate();

    /** React states */
    const [paymentLink, setPaymentLink] = useState<PaymentLink | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isRedirecting, setIsRedirecting] = useState<boolean>(false);

    /** Load the payment link data on mount and when uniqueLinkId changes */
    useEffect(() =>
    {
        loadPaymentLink();
    }, [props.uniqueLinkId]);

    /** Load the payment link by unique ID */
    const loadPaymentLink = async () =>
    {
        setIsLoading(true);
        setErrorMessage('');

        try
        {
            const link = await PaymentLink.getPaymentLinkByUniqueId(props.uniqueLinkId);

            if (link)
            {
                setPaymentLink(link);

                // Check if payment link is expired
                if (link.expiresAt)
                {
                    const expiryDate = new Date(link.expiresAt);
                    const now = new Date();

                    if (expiryDate < now)
                    {
                        setErrorMessage(t('PAYMENT_LINK_EXPIRED', props.language));
                    }
                }
            }
            else
            {
                setErrorMessage(t('PAYMENT_LINK_NOT_FOUND', props.language));
            }
        }
        catch (error)
        {
            console.error('Error loading payment link:', error);
            setErrorMessage(t('PAYMENT_LINK_LOAD_ERROR', props.language));
        }
        finally
        {
            setIsLoading(false);
        }
    };

    /** Handle payment button click - redirect to Netopia */
    const handlePaymentClick = async () =>
    {
        if (!paymentLink) return;

        setIsRedirecting(true);

        try
        {
            // Check if payment link has a valid ID
            if (!paymentLink.id)
            {
                setIsRedirecting(false);
                setErrorMessage(t('PAYMENT_LINK_INVALID', props.language));
                return;
            }

            // Create Netopia payment gateway instance
            const netopia = new NetopiaPaymentGateway();

            // Build redirect and cancel URLs
            const redirectUrl = UrlHelper.instance.getPaymentLinkConfirmUrl(props.language, paymentLink.uniqueLinkId, false);

            const paymentUrl = await netopia.startPaymentRequest(
                paymentLink.amount,
                paymentLink.currency,
                PAYMENT_LINK_ORDER_ID_PREFIX + paymentLink.id.toString(), // Use payment link's ID as order ID with prefix so Netopia doesn't confuse it with an other type of order that has the same ID
                paymentLink.description || t('PAYMENT_FOR_HOTEL_FENYO', props.language),
                paymentLink.customerEmail,
                paymentLink.customerPhone || '',
                redirectUrl
            );

            if (paymentUrl)
            {
                // Redirect to the Netopia payment page
                window.location.href = paymentUrl;
            }
            else
            {
                // Payment initialization failed
                setIsRedirecting(false);
                setErrorMessage(t('PAYMENT_INITIALIZATION_ERROR', props.language));
            }
        }
        catch (error)
        {
            console.error('Error starting payment:', error);
            setIsRedirecting(false);
            setErrorMessage(t('PAYMENT_INITIALIZATION_ERROR', props.language));
        }
    };


    /** Get status color */
    const getStatusColor = (status: PaymentLinkStatus): string =>
    {
        switch (status)
        {
            case PaymentLinkStatus.PAID:
                return 'success';
            case PaymentLinkStatus.PAYMENT_FAILED:
                return 'danger';
            case PaymentLinkStatus.PAYMENT_PENDING:
                return 'warning';
            default:
                return 'info';
        }
    };

    /** Get status text */
    const getStatusText = (status: PaymentLinkStatus): string =>
    {
        switch (status)
        {
            case PaymentLinkStatus.PAID:
                return t('PAYMENT_STATUS_PAID', props.language);
            case PaymentLinkStatus.PAYMENT_FAILED:
                return t('PAYMENT_STATUS_FAILED', props.language);
            case PaymentLinkStatus.PAYMENT_PENDING:
                return t('PAYMENT_STATUS_PENDING', props.language);
            case PaymentLinkStatus.CREATED:
                return t('PAYMENT_STATUS_CREATED', props.language);
            default:
                return status;
        }
    };

    /** Check if payment can be made */
    const canMakePayment = (): boolean =>
    {
        if (!paymentLink) return false;
        if (paymentLink.status === PaymentLinkStatus.PAID) return false;

        // Check if expired
        if (paymentLink.expiresAt)
        {
            const expiryDate = new Date(paymentLink.expiresAt);
            const now = new Date();
            if (expiryDate < now) return false;
        }

        return true;
    };

    /** Loading state component */
    const cmpLoading = (
        <div className="text-center py-5">
            <Spinner animation="border" role="status">
                <span className="visually-hidden">{t('LOADING', props.language)}</span>
            </Spinner>
        </div>
    );

    /** Error state component */
    const cmpError = (
        <div className="text-center py-5">
            <Alert variant="danger">
                <h4>{t('ERROR', props.language)}</h4>
                <p>{errorMessage}</p>
            </Alert>
        </div>
    );

    /** Payment information display component */
    const cmpPaymentInfo = paymentLink && (
        <div className="payment-info-container">
            {/* Status Badge */}
            {paymentLink.status != PaymentLinkStatus.CREATED &&
                <div className="mb-4">
                    <Alert variant={getStatusColor(paymentLink.status)}>
                        <strong>{t('STATUS', props.language)}:</strong> {getStatusText(paymentLink.status)}
                    </Alert>
                </div>
            }

            {/* Payment Details */}
            <PaymentDetailsSection title={t('PAYMENT_DETAILS', props.language)}>
                <PaymentDetailRow 
                    label={t('AMOUNT', props.language)} 
                    value={formatPrice(paymentLink.amount, true, paymentLink.currency)}
                />

                {paymentLink.description && (
                    <PaymentDetailRow 
                        label={t('DESCRIPTION', props.language)} 
                        value={paymentLink.description}
                        isMultiline={true}
                    />
                )}
            </PaymentDetailsSection>

            {/* Customer Information */}
            {(paymentLink.customerName || paymentLink.customerEmail || paymentLink.customerPhone) && (
                <PaymentDetailsSection title={t('CUSTOMER_INFORMATION', props.language)}>
                    {paymentLink.customerName && (
                        <PaymentDetailRow 
                            label={t('NAME', props.language)} 
                            value={paymentLink.customerName}
                        />
                    )}

                    {paymentLink.customerEmail && (
                        <PaymentDetailRow 
                            label={t('EMAIL', props.language)} 
                            value={paymentLink.customerEmail}
                        />
                    )}

                    {paymentLink.customerPhone && (
                        <PaymentDetailRow 
                            label={t('PHONE', props.language)} 
                            value={paymentLink.customerPhone}
                        />
                    )}
                </PaymentDetailsSection>
            )}

            {/* Expiry Information */}
            {paymentLink.expiresAt && (
                <div className="expiry-info mb-4">
                    <Alert variant="warning">
                        <strong>{t('EXPIRES_AT', props.language)}:</strong>
                        <span className="ms-2">
                            {formatDateUserFriendly(paymentLink.expiresAt, props.language)}
                        </span>
                    </Alert>
                </div>
            )}

            {/* Payment Button */}
            {canMakePayment() && (
                <div className="payment-action text-center">
                    <Button
                        variant="primary"
                        size="lg"
                        onClick={handlePaymentClick}
                        disabled={isRedirecting}
                        className="px-5 py-3 text-uppercase fw-semibold"
                    >
                        {isRedirecting ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                {t('REDIRECTING_TO_PAYMENT', props.language)}
                            </>
                        ) : (
                            t('PROCEED_TO_PAYMENT', props.language)
                        )}
                    </Button>

                    <div className="mt-3">
                        <small className="text-muted">
                            {t('PAYMENT_SECURE_MESSAGE', props.language)}
                        </small>
                    </div>
                </div>
            )}

            {/* Already Paid Message */}
            {paymentLink.status === PaymentLinkStatus.PAID && (
                <div className="text-center">
                    <Alert variant="success">
                        <h4>{t('PAYMENT_ALREADY_COMPLETED', props.language)}</h4>
                        <p>{t('PAYMENT_ALREADY_COMPLETED_MESSAGE', props.language)}</p>
                    </Alert>
                </div>
            )}

            {/* Payment Failed Message */}
            {paymentLink.status === PaymentLinkStatus.PAYMENT_FAILED && (
                <div className="text-center">
                    <Alert variant="danger">
                        <h4>{t('PAYMENT_FAILED', props.language)}</h4>
                        <p>{paymentLink.paymentGatewayMessage || t('PAYMENT_FAILED_MESSAGE', props.language)}</p>
                    </Alert>

                    {canMakePayment() && (
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={handlePaymentClick}
                            disabled={isRedirecting}
                            className="mt-3 px-5 py-3 text-uppercase fw-semibold"
                        >
                            {t('TRY_AGAIN', props.language)}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );

    /** Render */
    return (
        <>
            <PaymentLinkCard title={t('PAYMENT_LINK_TITLE', props.language)}>
                {isLoading && cmpLoading}
                {!isLoading && errorMessage && cmpError}
                {!isLoading && !errorMessage && paymentLink && cmpPaymentInfo}
            </PaymentLinkCard>

            <PaymentContactSupport language={props.language} />
        </>
    );
}

export default PaymentLinkStartPayment;